import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Construction as ConstructionIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as FileDownloadIcon,
  History as HistoryIcon,
  LibraryBooks as LibraryBooksIcon,
} from '@mui/icons-material';
import api from '../api';
import { formatYuan } from '../utils/money';
import { getStatusText, getStatusColor, nextStatuses } from '../utils/quotationStatus';
import { showSuccess, showError, showWarning } from '../components/GlobalSnackbar';

const EMPTY_ROW = { isNew: true, itemId: null, sectionId: null, name: '', specification: '', unit: '', quantity: '', materialPrice: '', laborPrice: '', machineryPrice: '', priceSource: 'manual', priceSourceRef: '', priceSourceDate: '' };

// 单价库弹窗渲染行数封顶（slice 与提示文案共用同一常量）
const PICKER_MAX_ROWS = 100;

// 单价依据来源（与后端 PRICE_SOURCES 枚举一致；value 同时是 API 存储值）
const PRICE_SOURCE_OPTIONS = [
  { value: 'quota', label: '单价库（历史成交/定额）' },
  { value: 'info_price', label: '山西信息价' },
  { value: 'market', label: '市场询价' },
  { value: 'history', label: '历史存档' },
  { value: 'manual', label: '人工估价（须注明校核口径）' },
];

// 今天（本地时区）YYYY-MM-DD，供依据日期默认值
const todayStr = () => new Date(Date.now() - new Date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(!id);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isCreate = !id;
  const isArchived = quotation?.status === 'ARCHIVED';

  // 明细行编辑状态（一次一行）
  const [rowForm, setRowForm] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [addingSection, setAddingSection] = useState(false);
  const [busy, setBusy] = useState(false);
  // 写操作互斥锁：setState 异步，busy 状态挡不住同帧双击，必须用 ref 同步守卫
  const writeLock = useRef(false);

  // 单价库弹窗（tab = quota 单价库 / info 山西信息价）
  const [quotaPicker, setQuotaPicker] = useState({ open: false });
  const [quotaItems, setQuotaItems] = useState([]);
  const [quotaSearch, setQuotaSearch] = useState('');
  const [pickerTab, setPickerTab] = useState('quota');
  const [infoPrices, setInfoPrices] = useState([]);
  const [infoSearch, setInfoSearch] = useState('');

  // 报价-项目关联（结算闭环）：编辑表头时的可选项目清单
  const [projects, setProjects] = useState([]);

  // 结算回填（净利率闭环）：视图数据 + 覆盖值表单
  const [settlement, setSettlement] = useState(null);
  const [settleForm, setSettleForm] = useState({ settledActualCost: '', settledIncome: '', settledNote: '' });
  const [settleSaving, setSettleSaving] = useState(false);
  // 成交锚点回写（迭代D）：本单明细按成交价写进单价库，AI 报价从此参照真实成交价
  const [anchorWriting, setAnchorWriting] = useState(false);

  // AI 多轮对话：粘贴工程描述 → 与 AI 讨论方案/价格 → 一键生成结构导入
  // clarifying/questions：成单前歧义确认（#14 幻影101m 教训）
  const [aiDialog, setAiDialog] = useState({ open: false, messages: [], input: '', sending: false, generating: false, importing: false, result: null, clarifying: false, questions: [], error: null });

  // 工艺模板向导（B1）：选模板 → 填参数 → 后端求值展开成分部+明细
  const [assemblyDlg, setAssemblyDlg] = useState({ open: false, templates: null, selected: null, params: {}, applying: false });
  // 修订历史抽屉（B2）：版本列表 + 与当前的 diff
  const [revDrawer, setRevDrawer] = useState({ open: false, list: [], diff: null, loading: false });

  useEffect(() => {
    if (id) {
      fetchQuotation();
      fetchSettlement();
    } else {
      setLoading(false);
      setEditForm({
        projectName: '', buildUnit: '', quoteUnit: '', contractMethod: '包工包料，综合单价包干',
        quoteDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10), validDays: 30,
        managementFeeRate: 3, profitRate: 3, capitalCostRate: 0,
        techMeasureRate: 0, orgMeasureRate: 5,
        socialInsuranceRate: 0, housingFundRate: 0, workInjuryRate: 0,
        riskFeeRate: 0, taxRate: 9,
        projectId: '',
      });
    }
    // 项目清单：建单/编辑时供「关联项目」下拉（结算闭环）
    api.get('/api/projects?pageSize=500')
      .then((res) => setProjects(res.data.projects || res.data || []))
      .catch(() => {/* 关联项目是可选功能，加载失败不阻塞建单 */});
  }, [id]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/quotations/${id}`);
      setQuotation(response.data);
      // 自动触发计算（ARCHIVED 历史存档为快照，后端会拒绝重算，忽略即可）
      if (response.data.status !== 'ARCHIVED') {
        try {
          await api.post(`/api/quotations/${id}/calculate`);
          const refreshed = await api.get(`/api/quotations/${id}`);
          setQuotation(refreshed.data);
        } catch (calcErr) {
          console.error('Calculation error:', calcErr);
        }
      }
    } catch (error) {
      console.error('Error fetching quotation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = () => {
    setEditForm({
      projectName: quotation.projectName || '',
      buildUnit: quotation.buildUnit || '',
      quoteUnit: quotation.quoteUnit || '',
      contractMethod: quotation.contractMethod || '',
      quoteDate: quotation.quoteDate || '',
      validDays: quotation.validDays || 30,
      managementFeeRate: quotation.managementFeeRate ?? 0,
      profitRate: quotation.profitRate ?? 0,
      capitalCostRate: quotation.capitalCostRate ?? 0,
      techMeasureRate: quotation.techMeasureRate ?? 0,
      orgMeasureRate: quotation.orgMeasureRate ?? 0,
      socialInsuranceRate: quotation.socialInsuranceRate ?? 0,
      housingFundRate: quotation.housingFundRate ?? 0,
      workInjuryRate: quotation.workInjuryRate ?? 0,
      riskFeeRate: quotation.riskFeeRate ?? 0,
      taxRate: quotation.taxRate ?? 9,
      projectId: quotation.projectId || '',
    });
    setEditing(true);
  };

  // 结算视图（报价-成本闭环）：失败不阻塞主流程（老库未迁移/未关联项目都属正常）
  const fetchSettlement = async () => {
    try {
      const res = await api.get(`/api/quotations/${id}/settlement`);
      setSettlement(res.data);
      setSettleForm({
        settledActualCost: res.data.overrides.settledActualCostYuan ?? '',
        settledIncome: res.data.overrides.settledIncomeYuan ?? '',
        settledNote: res.data.overrides.settledNote || '',
      });
    } catch (e) {
      setSettlement(null);
    }
  };

  // 结算回填保存：空串=清空覆盖值（回到项目汇总口径）；ARCHIVED 单也允许（事后记账不动价格快照）
  const saveSettlement = async () => {
    if (writeLock.current) return;
    try {
      writeLock.current = true;
      setSettleSaving(true);
      await api.put(`/api/quotations/${id}/settlement`, {
        settledActualCost: settleForm.settledActualCost === '' ? null : Number(settleForm.settledActualCost),
        settledIncome: settleForm.settledIncome === '' ? null : Number(settleForm.settledIncome),
        settledNote: settleForm.settledNote,
      });
      await fetchSettlement();
      showSuccess('结算已保存');
  } catch (e) {
    showError(e.response?.data?.error || '保存结算失败', 'error');
  } finally {
      writeLock.current = false;
      setSettleSaving(false);
    }
  };

  // 回写成交锚点：幂等由后端把守（同单重复回写 409）；ARCHIVED 单也允许——成交价恰恰来自已完成的单
  const handleWriteAnchors = async () => {
    if (!window.confirm('把本单明细按成交价回写进单价库（成交锚点）？回写后 AI 报价将参照这些真实成交价。')) return;
    setAnchorWriting(true);
    try {
      const res = await api.post(`/api/quotations/${id}/anchor-from-settlement`);
      showSuccess(`已回写 ${res.data.created} 条成交锚点（${res.data.codes[0]} 起）`);
    } catch (e) {
      showError(e.response?.data?.error || '回写失败', 'error');
    } finally {
      setAnchorWriting(false);
    }
  };

  // ---------- 工艺模板向导（B1） ----------
  const openAssemblyDlg = async () => {
    setAssemblyDlg({ open: true, templates: null, selected: null, params: {}, applying: false });
    try {
      const res = await api.get('/api/assembly-templates');
      setAssemblyDlg((d) => ({ ...d, templates: res.data || [] }));
    } catch (e) {
      showError(e.response?.data?.error || '工艺模板加载失败', 'error');
      setAssemblyDlg((d) => ({ ...d, templates: [] }));
    }
  };

  const applyAssembly = async () => {
    const d = assemblyDlg;
    if (!d.selected || writeLock.current) return;
    const params = {};
    (d.selected.paramsSchema || []).forEach((p) => {
      const raw = d.params[p.key];
      params[p.key] = raw === undefined || raw === '' ? Number(p.default) : Number(raw);
    });
    try {
      writeLock.current = true;
      setAssemblyDlg((s) => ({ ...s, applying: true }));
      const res = await api.post(`/api/quotations/${id}/apply-assembly`, { templateId: d.selected.id, params });
      setAssemblyDlg({ open: false, templates: null, selected: null, params: {}, applying: false });
      await fetchQuotation();
      showSuccess(`已展开「${d.selected.name}」${res.data.itemCount} 行（含税总价 ${formatYuan(res.data.totalAmount)}），行数与单价可再逐行微调`);
    } catch (e) {
      showError(e.response?.data?.error || '应用工艺模板失败', 'error');
    } finally {
      writeLock.current = false;
      setAssemblyDlg((s) => ({ ...s, applying: false }));
    }
  };

  // ---------- 修订历史（B2） ----------
  const openRevisions = async () => {
    setRevDrawer({ open: true, list: [], diff: null, loading: true });
    try {
      const res = await api.get(`/api/quotations/${id}/revisions`);
      setRevDrawer((d) => ({ ...d, list: res.data.revisions || [], loading: false }));
    } catch (e) {
      showError(e.response?.data?.error || '修订历史加载失败', 'error');
      setRevDrawer((d) => ({ ...d, loading: false }));
    }
  };

  const loadRevisionDiff = async (revId) => {
    try {
      setRevDrawer((d) => ({ ...d, loading: true }));
      const res = await api.get(`/api/quotations/${id}/revisions/${revId}/diff`);
      setRevDrawer((d) => ({ ...d, diff: res.data, loading: false }));
    } catch (e) {
      showError(e.response?.data?.error || '对比加载失败', 'error');
      setRevDrawer((d) => ({ ...d, loading: false }));
    }
  };

  const saveRevisionNow = async () => {
    const note = window.prompt('修订说明（如：第三轮压价，B分项下调）', '') ?? '';
    if (note === null) return;
    try {
      const res = await api.post(`/api/quotations/${id}/revisions`, { changeNote: note || '手动存档' });
      showSuccess(`已存修订版 rev${res.data.revNo}`);
      if (revDrawer.open) openRevisions();
    } catch (e) {
      showError(e.response?.data?.error || '存档失败', 'error');
    }
  };

  // ---------- 状态流转（C3） ----------
  const changeQuotationStatus = async (target) => {
    const note = window.prompt(`流转到「${getStatusText(target)}」的备注（可留空）`, '');
    if (note === null) return;
    try {
      await api.patch(`/api/quotations/${id}/status`, { status: target, note: note || undefined });
      await fetchQuotation();
      showSuccess(`已流转到「${getStatusText(target)}」`);
    } catch (e) {
      showError(e.response?.data?.error || '状态流转失败', 'error');
    }
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      if (isCreate) {
        const res = await api.post('/api/quotations', editForm);
        const newId = res.data.id;
        showSuccess('报价单已创建，现在可以添加分部与明细');
        navigate(`/quotations/${newId}`);
      } else {
        await api.put(`/api/quotations/${id}`, editForm);
        await api.post(`/api/quotations/${id}/calculate`);
        const refreshed = await api.get(`/api/quotations/${id}`);
        setQuotation(refreshed.data);
        setEditing(false);
        showSuccess('已保存');
      }
    } catch (error) {
      console.error('Error saving quotation:', error);
      showSuccess(error.response?.data?.error || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---------- 明细分部 / 行操作 ----------
  const addSection = async () => {
    const name = newSectionName.trim();
    if (!name || writeLock.current) return;
    try {
      writeLock.current = true;
      setBusy(true);
      await api.post(`/api/quotations/${id}/sections`, { name });
      setNewSectionName('');
      setAddingSection(false);
      await fetchQuotation();
      showSuccess(`分部「${name}」已添加`);
    } catch (e) {
      showError(e.response?.data?.error || '添加分部失败', 'error');
    } finally {
      writeLock.current = false;
      setBusy(false);
    }
  };

  const deleteSection = async (sectionId, sectionName) => {
    if (!window.confirm(`确定删除分部「${sectionName}」及其全部明细行？`)) return;
    // 与添加/保存同款双击守卫：setState 挡不住同帧双击，删除也必须走 ref 锁
    if (writeLock.current) return;
    try {
      writeLock.current = true;
      setBusy(true);
      await api.delete(`/api/quotations/${id}/sections/${sectionId}`);
      // 正在编辑的行若属于被删分部，一并关闭，否则幽灵表单会锁死「明细行」按钮
      setRowForm((f) => (f && f.sectionId === sectionId ? null : f));
      await fetchQuotation();
      showSuccess('分部已删除');
    } catch (e) {
      showError(e.response?.data?.error || '删除分部失败', 'error');
    } finally {
      writeLock.current = false;
      setBusy(false);
    }
  };

  const startAddRow = (sectionId) => setRowForm({ ...EMPTY_ROW, sectionId });
  const startEditRow = (item) => setRowForm({
    isNew: false, itemId: item.id, sectionId: item.sectionId,
    name: item.name || '', specification: item.specification || '', unit: item.unit || '',
    quantity: item.quantity ?? '', materialPrice: item.materialPrice ?? '',
    laborPrice: item.laborPrice ?? '', machineryPrice: item.machineryPrice ?? '',
    priceSource: item.priceSource || 'manual',
    priceSourceRef: item.priceSourceRef || '',
    priceSourceDate: (item.priceSourceDate || '').slice(0, 10),
  });
  const cancelRow = () => setRowForm(null);

  const saveRow = async () => {
    const f = rowForm;
    if (!f.name.trim() || !f.unit.trim()) {
      showError('名称和单位不能为空');
      return;
    }
    if (writeLock.current) return;
    writeLock.current = true;
    const body = {
      sectionId: f.sectionId, name: f.name.trim(), specification: f.specification || null,
      unit: f.unit.trim(), quantity: Number(f.quantity) || 0,
      materialPrice: Number(f.materialPrice) || 0, laborPrice: Number(f.laborPrice) || 0,
      machineryPrice: Number(f.machineryPrice) || 0,
      priceSource: f.priceSource || 'manual',
      priceSourceRef: (f.priceSourceRef || '').trim() || null,
      priceSourceDate: (f.priceSourceDate || '').trim() || null,
    };
    try {
      setBusy(true);
      if (f.isNew) {
        await api.post(`/api/quotations/${id}/items`, body);
      } else {
        await api.put(`/api/quotations/${id}/items/${f.itemId}`, body);
      }
      setRowForm(null);
      await fetchQuotation();
      showSuccess(f.isNew ? '明细行已添加' : '明细行已保存');
    } catch (e) {
      showError(e.response?.data?.error || '保存明细行失败', 'error');
    } finally {
      writeLock.current = false;
      setBusy(false);
    }
  };

  const deleteRow = async (itemId) => {
    if (!window.confirm('确定删除这行明细？')) return;
    if (writeLock.current) return;
    try {
      writeLock.current = true;
      setBusy(true);
      await api.delete(`/api/quotations/${id}/items/${itemId}`);
      await fetchQuotation();
      showSuccess('明细行已删除');
    } catch (e) {
      showError(e.response?.data?.error || '删除失败', 'error');
    } finally {
      writeLock.current = false;
      setBusy(false);
    }
  };

  // ---------- 单价库/信息价带入 ----------
  const openQuotaPicker = async (tab = 'quota') => {
    setQuotaPicker({ open: true });
    setPickerTab(tab);
    if (tab === 'quota' && quotaItems.length === 0) {
      try {
        const res = await api.get('/api/quota-items');
        setQuotaItems(res.data);
      } catch (e) {
        showError('单价库加载失败');
      }
    }
    if (tab === 'info' && infoPrices.length === 0) {
      try {
        // 既有端点返回裸数组（server.js GET /api/info-prices）
        const res = await api.get('/api/info-prices', { params: { limit: 500 } });
        setInfoPrices(Array.isArray(res.data) ? res.data : (res.data.items || []));
      } catch (e) {
        showError('信息价加载失败');
      }
    }
  };

  // 依据落进项目特征（守则第3条「写进项目特征」）：已含「价格依据」则不重复追加
  const appendBasisToSpec = (spec, basisText) => {
    const s = (spec || '').trim();
    if (s.includes('价格依据：')) return s;
    return s ? `${s}；价格依据：${basisText}` : `价格依据：${basisText}`;
  };

  const pickQuota = (it) => {
    // 口径守卫：单价库的历史条目人材机三价全为 0、只有全费用综合价（含管理/利润/措施/税）。
    // 综合价不能直接拆进三价——calculate 费率链会再叠加 ~22% 双重加价，所以只带入名称/规格/单位。
    const bareSum = (Number(it.materialPrice) || 0) + (Number(it.laborPrice) || 0) + (Number(it.machineryPrice) || 0);
    const comp = Number(it.comprehensivePrice) || 0;
    const hasBarePrice = bareSum > 0;
    // 结构化依据（lint 依据）：来源=单价库 + 编码；同时把依据文案写进项目特征随单导出
    const quotaRef = `单价库 ${it.quotaCode || '未编号'}（${it.source || it.projectName || '历史成交'}）`;
    setRowForm((f) => ({
      ...f,
      name: it.name || f.name,
      specification: appendBasisToSpec(it.specification || f.specification, quotaRef),
      unit: it.unit || f.unit,
      ...(hasBarePrice
        ? {
            materialPrice: Number(it.materialPrice) || 0,
            laborPrice: Number(it.laborPrice) || 0,
            machineryPrice: Number(it.machineryPrice) || 0,
          }
        : {}),
      priceSource: 'quota',
      priceSourceRef: quotaRef,
      priceSourceDate: (it.quotedAt || '').slice(0, 10) || todayStr(),
    }));
    setQuotaPicker({ open: false });
    if (hasBarePrice) {
      showSuccess(`已带入「${it.name}」人材机裸价${comp > 0 ? `（综合价参考 ${formatYuan(comp)}）` : ''}${it.projectName ? ` · 来源：${it.projectName}` : ''}，依据已自动落档`);
    } else {
      showWarning(`已带入「${it.name}」名称/规格/单位。该条目仅存综合价 ${formatYuan(comp)}（已含费率），请手工填写人材机裸价`);
    }
  };

  // 信息价带入：官方价只覆盖材料价，人/机仍需手工；期次（如 2026-05~06）转日期供 lint 时效检查
  const pickInfoPrice = (it) => {
    const parts = String(it.period || '').split('-');
    const tail = parts.length === 2 ? parts[1].split('~') : [];
    const periodDate = (parts.length === 2 && tail.length === 2)
      ? `${parts[0]}-${tail[1]}-28`
      : todayStr();
    const infoRef = `山西信息价 ${it.period || ''}${it.specification ? `（${it.specification}）` : ''}`;
    setRowForm((f) => ({
      ...f,
      name: f.name || it.materialName,
      unit: f.unit || (it.unit && it.unit !== '见原文' ? it.unit : ''),
      materialPrice: Number(it.priceTaxed) || 0,
      specification: appendBasisToSpec(f.specification, infoRef),
      priceSource: 'info_price',
      priceSourceRef: infoRef,
      priceSourceDate: periodDate,
    }));
    setQuotaPicker({ open: false });
    showWarning(`已带入「${it.materialName}」材料价 ${Number(it.priceTaxed).toFixed(2)} 元（${it.period}），人工/机械价请补填`);
  };

  // ---------- AI 多轮对话 + 解析 ----------
  const openAiDialog = () => setAiDialog({ open: true, messages: [], input: '', sending: false, generating: false, importing: false, result: null, clarifying: false, questions: [], warnings: [], research: false, error: null });

  const sendAiChat = async () => {
    const text = aiDialog.input.trim();
    if (!text || aiDialog.sending) return;
    const nextMessages = [...aiDialog.messages, { role: 'user', content: text }];
    setAiDialog((d) => ({ ...d, messages: nextMessages, input: '', sending: true, error: null, result: null }));
    try {
      const res = await api.post('/api/ai/chat', { messages: nextMessages, research: aiDialog.research });
      setAiDialog((d) => ({ ...d, sending: false, messages: [...d.messages, { role: 'assistant', content: res.data.reply }] }));
    } catch (e) {
      const err = e.response?.data?.error || `发送失败：${e.message}`;
      setAiDialog((d) => ({ ...d, sending: false, error: err.includes('未配置') ? 'NO_KEY' : err }));
    }
  };

  // 先问后出单（#14 幻影101m 教训）：语音数量词、照片归属等疑点先确认，再 extract。
  // 答疑走普通聊天消息（sendAiChat），不另开通道
  const runClarify = async () => {
    if (aiDialog.messages.length < 2 || aiDialog.clarifying) return;
    setAiDialog((d) => ({ ...d, clarifying: true, error: null, questions: [] }));
    try {
      const res = await api.post('/api/ai/chat', { messages: aiDialog.messages, clarify: true });
      setAiDialog((d) => ({ ...d, clarifying: false, questions: res.data.questions || [] }));
    } catch (e) {
      const err = e.response?.data?.error || `核对失败：${e.message}`;
      setAiDialog((d) => ({ ...d, clarifying: false, error: err.includes('未配置') ? 'NO_KEY' : err }));
    }
  };

  // 把整段讨论一键收敛成报价结构（服务端追加 JSON 输出指令并归一化）。
  // 服务端同时跑价格护栏：缺价/超历史锚点±40% 的行以 warnings 返回，导入前必须人工过目
  const generateFromChat = async () => {
    if (aiDialog.messages.length < 2 || aiDialog.generating) return;
    setAiDialog((d) => ({ ...d, generating: true, error: null, result: null, warnings: [] }));
    try {
      const res = await api.post('/api/ai/chat', { messages: aiDialog.messages, extractQuotation: true });
      if (!res.data.quotation?.sections?.length) {
        setAiDialog((d) => ({ ...d, generating: false, error: 'AI 没能从讨论中生成结构，请补充工程量/做法信息后重试' }));
        return;
      }
      setAiDialog((d) => ({ ...d, generating: false, result: res.data.quotation, warnings: res.data.warnings || [] }));
    } catch (e) {
      const err = e.response?.data?.error || `生成失败：${e.message}`;
      setAiDialog((d) => ({ ...d, generating: false, error: err.includes('未配置') ? 'NO_KEY' : err }));
    }
  };

  const handleAiImport = async () => {
    const result = aiDialog.result;
    if (!result?.sections?.length) return;
    if (writeLock.current) return;
    writeLock.current = true;
    try {
      setAiDialog((d) => ({ ...d, importing: true }));
      await api.post(`/api/quotations/${id}/sections/batch`, { sections: result.sections });
      setAiDialog({ open: false, messages: [], input: '', sending: false, generating: false, importing: false, result: null, clarifying: false, questions: [], warnings: [], research: false, error: null });
      await fetchQuotation();
      showSuccess(`AI 已导入 ${result.sections.length} 个分部，请逐行核对数量与单价`);
    } catch (e) {
      setAiDialog((d) => ({ ...d, importing: false, error: e.response?.data?.error || '导入失败，请重试' }));
    } finally {
      writeLock.current = false;
    }
  };

  // 裸价合计预览：与后端 r2 同口径舍入，避免编辑时看到的合计与保存后不一致
  const rowTotal = rowForm
    ? Math.round((((Number(rowForm.materialPrice) || 0) + (Number(rowForm.laborPrice) || 0) + (Number(rowForm.machineryPrice) || 0)) * (Number(rowForm.quantity) || 0)) * 100) / 100
    : 0;

  // 单价库过滤缓存：行编辑的每次击键都会触发重渲染，
  // 不 useMemo 的话每帧都对全量单价库做 4 字段扫描
  const quotaFiltered = useMemo(() => {
    const kw = quotaSearch.trim().toLowerCase();
    if (!kw) return quotaItems;
    return quotaItems.filter((it) =>
      [it.name, it.specification, it.projectName, it.category]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(kw))
    );
  }, [quotaItems, quotaSearch]);

  // 分部→明细行索引：渲染层每分部两次 items.filter 是 O(分部×明细)，
  // 历史存档单明细多，一次建 Map 后 O(1) 查找（与后端 GET 组装同思路）
  const itemsBySection = useMemo(() => {
    const m = new Map();
    (quotation?.items || []).forEach((it) => {
      const arr = m.get(it.sectionId);
      if (arr) arr.push(it);
      else m.set(it.sectionId, [it]);
    });
    return m;
  }, [quotation]);


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!quotation && !isCreate) {
    return (
      <Box>
        <Typography variant="h6">报价单不存在</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotations')}>
          返回列表
        </Button>
      </Box>
    );
  }

  const renderRowForm = (sectionId) => (
    <>
    <TableRow>
      <TableCell />
      <TableCell sx={{ minWidth: 160 }}>
        <TextField size="small" fullWidth value={rowForm.name}
          onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })} placeholder="项目名称" />
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>
        <TextField size="small" fullWidth value={rowForm.specification}
          onChange={(e) => setRowForm({ ...rowForm, specification: e.target.value })} placeholder="规格" />
      </TableCell>
      <TableCell sx={{ width: 90 }}>
        <TextField size="small" fullWidth value={rowForm.unit}
          onChange={(e) => setRowForm({ ...rowForm, unit: e.target.value })} placeholder="单位" />
      </TableCell>
      <TableCell sx={{ width: 100 }}>
        <TextField size="small" fullWidth type="number" value={rowForm.quantity}
          onChange={(e) => setRowForm({ ...rowForm, quantity: e.target.value })} placeholder="数量" />
      </TableCell>
      <TableCell sx={{ width: 110 }}>
        <TextField size="small" fullWidth type="number" value={rowForm.materialPrice}
          onChange={(e) => setRowForm({ ...rowForm, materialPrice: e.target.value })} placeholder="材料" />
      </TableCell>
      <TableCell sx={{ width: 110 }}>
        <TextField size="small" fullWidth type="number" value={rowForm.laborPrice}
          onChange={(e) => setRowForm({ ...rowForm, laborPrice: e.target.value })} placeholder="人工" />
      </TableCell>
      <TableCell sx={{ width: 110 }}>
        <TextField size="small" fullWidth type="number" value={rowForm.machineryPrice}
          onChange={(e) => setRowForm({ ...rowForm, machineryPrice: e.target.value })} placeholder="机械" />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight="bold">{formatYuan(rowTotal)}</Typography>
        <Typography variant="caption" color="text.secondary">裸价合计</Typography>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Button size="small" startIcon={<LibraryBooksIcon />} onClick={() => openQuotaPicker('quota')} sx={{ mr: 1 }}>
          单价库
        </Button>
        <Button size="small" variant="contained" onClick={saveRow} disabled={busy} sx={{ mr: 1 }}>
          保存
        </Button>
        <Button size="small" onClick={cancelRow} disabled={busy}>取消</Button>
      </TableCell>
    </TableRow>
    {/* 单价依据子行（守则第3条）：无依据的行会被出门 lint 拦下，manual 必须给校核说明 */}
    <TableRow sx={{ backgroundColor: 'grey.100' }}>
      <TableCell />
      <TableCell colSpan={9}>
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <TextField
            select size="small" label="单价依据" sx={{ minWidth: 220 }}
            value={rowForm.priceSource || 'manual'}
            onChange={(e) => setRowForm({ ...rowForm, priceSource: e.target.value })}
          >
            {PRICE_SOURCE_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="依据说明（定额编号/信息价期次/询价口径）" sx={{ minWidth: 300, flex: 1 }}
            value={rowForm.priceSourceRef || ''}
            onChange={(e) => setRowForm({ ...rowForm, priceSourceRef: e.target.value })}
            placeholder={rowForm.priceSource === 'manual' ? '必填：如「按2018市政定额人工挖沟槽校核」' : '选填：从单价库/信息价带入时自动填写'}
          />
          <TextField
            size="small" type="date" label="依据日期" sx={{ width: 160 }}
            InputLabelProps={{ shrink: true }}
            value={rowForm.priceSourceDate || ''}
            onChange={(e) => setRowForm({ ...rowForm, priceSourceDate: e.target.value })}
          />
          <Button size="small" onClick={() => openQuotaPicker('info')}>信息价选材料</Button>
        </Box>
        {(rowForm.priceSource || 'manual') === 'manual' && !(rowForm.priceSourceRef || '').trim() && (
          <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
            人工估价未写校核口径：保存可以，但出门导出会被闸门拦截
          </Typography>
        )}
      </TableCell>
    </TableRow>
    </>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/quotations')}
          >
            返回
          </Button>
          <Typography variant="h4">{isCreate ? '新建报价单' : '报价单详情'}</Typography>
        </Box>
        <Box>
          {!isCreate && (
            <>
              {!isArchived && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<AutoAwesomeIcon />}
                  sx={{ mr: 1 }}
                  onClick={openAiDialog}
                  title="与 AI 多轮沟通现场方案与价格，一键生成分部分项并导入"
                >
                  AI 报价顾问
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                sx={{ mr: 1 }}
                onClick={async () => {
                  try {
                    const response = await api.get(`/api/quotations/${id}/export-word`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `报价单_${quotation?.projectName || id}_可编辑.docx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('导出Word失败:', error);
                    alert('导出Word失败，请重试');
                  }
                }}
                title="可编辑 Word 版（商务谈判用）：客户砍价直接改数字"
              >
                导出 Word
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                sx={{ mr: 1 }}
                onClick={async () => {
                  try {
                    const response = await api.get(`/api/quotations/${id}/export`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `报价单_${quotation?.projectName || id}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('导出PDF失败:', error);
                    alert('导出PDF失败，请重试');
                  }
                }}
              >
                导出 PDF
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<FileDownloadIcon />}
                sx={{ mr: 1 }}
                onClick={async () => {
                  try {
                    const response = await api.get(`/api/quotations/${id}/export-excel`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `报价单_${quotation?.projectName || id}_广联达.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('导出Excel失败:', error);
                    alert('导出Excel失败，请重试');
                  }
                }}
                title="导出广联达GCCP 7.0标准Excel清单，可直接导入广联达"
              >
                导出广联达Excel
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<FileDownloadIcon />}
                sx={{ mr: 1 }}
                onClick={async () => {
                  try {
                    const response = await api.get(`/api/quotations/${id}/export-analysis`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `报价单_${quotation?.projectName || id}_内部分析表.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('导出分析表失败:', error);
                    alert('导出分析表失败，请重试');
                  }
                }}
                title="内部对账/审计用：三价构成+单价依据+取费链（不外发）"
              >
                分析表
              </Button>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                sx={{ mr: 1 }}
                onClick={openRevisions}
                title="修订版本列表与对比（发单版由导出脚本自动存档）"
              >
                修订历史
              </Button>
              <Button
                variant="outlined"
                onClick={saveRevisionNow}
                sx={{ mr: 1 }}
                title="把当前状态存一版快照（压价轮次/条款调整留痕）"
              >
                存修订版
              </Button>
            </>
          )}
          {editing ? (
            <>
              {!isCreate && (
                <Button variant="outlined" sx={{ mr: 1 }} onClick={() => setEditing(false)} disabled={saving}>
                  取消
                </Button>
              )}
              <Button variant="contained" onClick={handleEditSave} disabled={saving}>
                {saving ? '保存中...' : isCreate ? '创建报价单' : '保存'}
              </Button>
            </>
          ) : (
            !isArchived && (
              <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditStart}>
                编辑
              </Button>
            )
          )}
        </Box>
      </Box>

      {isArchived && (
        <Alert severity="info" sx={{ mb: 2 }}>
          此单为历史报价快照（全费用综合单价口径，总价为存档值），不支持编辑与重算；如需调整请新建报价单。
        </Alert>
      )}

      {editing ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>{isCreate ? '新建报价单' : '编辑报价单'}</Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField label="项目名称" value={editForm.projectName} onChange={(e) => setEditForm({...editForm, projectName: e.target.value})} size="small" fullWidth />
              <TextField label="建设单位" value={editForm.buildUnit} onChange={(e) => setEditForm({...editForm, buildUnit: e.target.value})} size="small" fullWidth />
              <TextField label="报价单位" value={editForm.quoteUnit} onChange={(e) => setEditForm({...editForm, quoteUnit: e.target.value})} size="small" fullWidth />
              <TextField label="承包方式" value={editForm.contractMethod} onChange={(e) => setEditForm({...editForm, contractMethod: e.target.value})} size="small" fullWidth />
              {/* 结算闭环：关联项目后，报表自动按项目汇总人工/材料/杂费成本对账净利率 */}
              <TextField
                select label="关联项目（用于结算对账，可不选）" size="small" fullWidth
                value={editForm.projectId || ''}
                onChange={(e) => setEditForm({ ...editForm, projectId: e.target.value })}
                helperText="关联后「报价结算」卡与净利率报表会自动拉取该项目的记账成本（人工+材料+杂费）与回款"
              >
                <MenuItem value="">不关联</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
              <Box display="flex" gap={2}>
                <TextField label="报价日期" type="date" value={editForm.quoteDate} onChange={(e) => setEditForm({...editForm, quoteDate: e.target.value})} size="small" InputLabelProps={{ shrink: true }} />
                <TextField label="有效期(天)" type="number" value={editForm.validDays} onChange={(e) => setEditForm({...editForm, validDays: parseInt(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
              </Box>
              <Divider />
              <Typography variant="subtitle2">
                费率设置（%）—— 默认为统一口径：管理3 / 利润3 / 组织措施5 / 税9，其余按需开启
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <TextField label="管理费率" type="number" value={editForm.managementFeeRate} onChange={(e) => setEditForm({...editForm, managementFeeRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="利润率" type="number" value={editForm.profitRate} onChange={(e) => setEditForm({...editForm, profitRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="垫资费率" type="number" value={editForm.capitalCostRate} onChange={(e) => setEditForm({...editForm, capitalCostRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="技术措施费率" type="number" value={editForm.techMeasureRate} onChange={(e) => setEditForm({...editForm, techMeasureRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="组织措施费率" type="number" value={editForm.orgMeasureRate} onChange={(e) => setEditForm({...editForm, orgMeasureRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="社保费率" type="number" value={editForm.socialInsuranceRate} onChange={(e) => setEditForm({...editForm, socialInsuranceRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="住房公积金率" type="number" value={editForm.housingFundRate} onChange={(e) => setEditForm({...editForm, housingFundRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="工伤保险率" type="number" value={editForm.workInjuryRate} onChange={(e) => setEditForm({...editForm, workInjuryRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="风险费率" type="number" value={editForm.riskFeeRate} onChange={(e) => setEditForm({...editForm, riskFeeRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
                <TextField label="增值税率" type="number" value={editForm.taxRate} onChange={(e) => setEditForm({...editForm, taxRate: parseFloat(e.target.value) || 0})} size="small" sx={{ width: 120 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="h5">{quotation.projectName}</Typography>
              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                <Chip
                  label={getStatusText(quotation.status)}
                  color={getStatusColor(quotation.status)}
                />
                {nextStatuses(quotation.status).map((s) => (
                  <Button key={s} size="small" variant="outlined" onClick={() => changeQuotationStatus(s)}>
                    → {getStatusText(s)}
                  </Button>
                ))}
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              建设单位：{quotation.buildUnit}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              报价单位：{quotation.quoteUnit}
            </Typography>
            {(quotation.linkedProjectName || quotation.projectId) && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                关联项目：{quotation.linkedProjectName || '（项目已删除）'}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              承包方式：{quotation.contractMethod || '包工包料，综合单价包干'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              报价日期：{quotation.quoteDate} | 有效期：{quotation.validDays}天
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* 报价结算卡（净利率闭环）：报价（元）↔ 项目记账成本（分→元）对账；成本未全记账时可手填覆盖 */}
      {!isCreate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">报价结算（净利率闭环）</Typography>
              {settlement?.computed?.meetsThreshold !== null && settlement?.computed?.meetsThreshold !== undefined && (
                <Chip
                  label={settlement.computed.meetsThreshold ? `净利率 ${settlement.computed.marginPct}% ≥ 30% 达标` : `净利率 ${settlement.computed.marginPct}% < 30% 不达标`}
                  color={settlement.computed.meetsThreshold ? 'success' : 'error'}
                  size="small"
                />
              )}
            </Box>
            {!settlement ? (
              <Typography variant="body2" color="text.secondary">
                结算数据不可用（未关联项目且未手填成本）。编辑报价单选择「关联项目」，或在下方手填实际成本。
              </Typography>
            ) : (
              <>
                <Box display="flex" flexWrap="wrap" gap={3} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">含税总价</Typography>
                    <Typography variant="h6">{formatYuan(settlement.quotation.totalAmountYuan)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      实际成本（{settlement.computed.costSource === 'override' ? '手填' : settlement.computed.costSource === 'system' ? '项目记账汇总' : '暂无'}）
                    </Typography>
                    <Typography variant="h6">{settlement.computed.actualCostYuan !== null ? formatYuan(settlement.computed.actualCostYuan) : '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">净利率（报价口径）</Typography>
                    <Typography variant="h6" color={settlement.computed.meetsThreshold ? 'success.main' : settlement.computed.meetsThreshold === false ? 'error.main' : 'text.primary'}>
                      {settlement.computed.marginPct !== null ? `${settlement.computed.marginPct}%` : '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">净利率（回款口径）</Typography>
                    <Typography variant="h6">{settlement.computed.cashMarginPct !== null ? `${settlement.computed.cashMarginPct}%` : '—'}</Typography>
                  </Box>
                </Box>
                {settlement.project && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    项目记账：人工 {formatYuan(settlement.project.laborCostYuan)} + 材料 {formatYuan(settlement.project.materialCostYuan)} + 杂费 {formatYuan(settlement.project.expenseCostYuan)}
                    ，已回款 {formatYuan(settlement.project.totalIncomeYuan || 0)}
                  </Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>结算回填（成本未全走系统记账时手填；填后优先于项目汇总）</Typography>
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start">
                  <TextField
                    size="small" type="number" label="实际成本（元）" sx={{ width: 180 }}
                    value={settleForm.settledActualCost}
                    onChange={(e) => setSettleForm({ ...settleForm, settledActualCost: e.target.value })}
                    placeholder="留空=用项目汇总"
                  />
                  <TextField
                    size="small" type="number" label="结算收入（元）" sx={{ width: 180 }}
                    value={settleForm.settledIncome}
                    onChange={(e) => setSettleForm({ ...settleForm, settledIncome: e.target.value })}
                    placeholder="留空=用项目回款"
                  />
                  <TextField
                    size="small" label="结算备注" sx={{ width: 320 }}
                    value={settleForm.settledNote}
                    onChange={(e) => setSettleForm({ ...settleForm, settledNote: e.target.value })}
                    placeholder="如：含签证增项、甲供材抵扣等口径说明"
                  />
                  <Button variant="outlined" size="small" disabled={settleSaving} onClick={saveSettlement}>
                    {settleSaving ? '保存中…' : '保存结算'}
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleWriteAnchors} disabled={anchorWriting}>
                    {anchorWriting ? '回写中…' : '回写成交锚点'}
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {!isCreate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">分项明细</Typography>
              {!isArchived && !addingSection && (
                <Box display="flex" gap={1}>
                  <Button size="small" variant="outlined" startIcon={<ConstructionIcon />} onClick={openAssemblyDlg}
                    title="高频工艺参数化展开：屋面SBS/排水管更换/落水管维修，历史成交价+依据自动带入">
                    从工艺模板添加
                  </Button>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setAddingSection(true)}>
                    添加分部
                  </Button>
                </Box>
              )}
            </Box>

            {!isArchived && addingSection && (
              <Box display="flex" gap={1} alignItems="center" mb={2}>
                <TextField
                  size="small" label="分部名称（如：分部分项工程 / 措施项目）"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addSection()}
                  sx={{ minWidth: 360 }}
                  autoFocus
                />
                <Button size="small" variant="contained" onClick={addSection} disabled={busy || !newSectionName.trim()}>
                  确定
                </Button>
                <Button size="small" onClick={() => { setAddingSection(false); setNewSectionName(''); }}>
                  取消
                </Button>
              </Box>
            )}

            {quotation.sections && quotation.sections.length > 0 ? (
              quotation.sections.map((section) => (
                <Box key={section.id} sx={{ mb: 3 }}>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.100' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {section.name}
                        </Typography>
                        {section.description && (
                          <Typography variant="body2" color="text.secondary">
                            {section.description}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                          小计：{formatYuan(section.subtotal)}
                        </Typography>
                        {!isArchived && (
                          <>
                            <Button
                              size="small" startIcon={<AddIcon />}
                              onClick={() => { setRowForm({ ...EMPTY_ROW, sectionId: section.id }); }}
                              disabled={!!rowForm}
                            >
                              明细行
                            </Button>
                            <IconButton size="small" onClick={() => deleteSection(section.id, section.name)} disabled={busy}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Paper>

                    <TableContainer component={Paper} sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>序号</TableCell>
                            <TableCell>项目名称</TableCell>
                            <TableCell>规格/说明</TableCell>
                            <TableCell>单位</TableCell>
                            <TableCell align="right">数量</TableCell>
                            <TableCell align="right">材料</TableCell>
                            <TableCell align="right">人工</TableCell>
                            <TableCell align="right">机械</TableCell>
                            <TableCell align="right">合价</TableCell>
                            <TableCell align="right">操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(itemsBySection.get(section.id) || [])
                            .map((item, index) => {
                              if (rowForm && !rowForm.isNew && rowForm.itemId === item.id) {
                                return <React.Fragment key={item.id}>{renderRowForm(section.id)}</React.Fragment>;
                              }
                              return (
                                <TableRow key={item.id} hover>
                                  <TableCell>{index + 1}</TableCell>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell>{item.specification || '-'}</TableCell>
                                  <TableCell>{item.unit}</TableCell>
                                  <TableCell align="right">
                                    {Number(item.quantity || 0).toFixed(1)}
                                  </TableCell>
                                  <TableCell align="right">{Number(item.materialPrice || 0) > 0 ? formatYuan(item.materialPrice) : '-'}</TableCell>
                                  <TableCell align="right">{Number(item.laborPrice || 0) > 0 ? formatYuan(item.laborPrice) : '-'}</TableCell>
                                  <TableCell align="right">{Number(item.machineryPrice || 0) > 0 ? formatYuan(item.machineryPrice) : '-'}</TableCell>
                                  <TableCell align="right">{formatYuan(item.totalPrice)}</TableCell>
                                  <TableCell align="right">
                                    {!isArchived && (
                                      <>
                                        <IconButton size="small" onClick={() => startEditRow(item)} disabled={busy || !!rowForm} title={rowForm ? '请先完成/取消当前行编辑' : ''}>
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => deleteRow(item.id)} disabled={busy || !!rowForm}>
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          {rowForm && rowForm.isNew && rowForm.sectionId === section.id && renderRowForm(section.id)}
                          {(itemsBySection.get(section.id) || []).length === 0 &&
                            (!rowForm || rowForm.sectionId !== section.id) && (
                            <TableRow>
                              <TableCell colSpan={10}>
                                <Typography variant="body2" color="text.secondary" align="center">
                                  暂无明细行，点击上方「明细行」按钮添加
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                {isArchived ? '此历史存档单没有分部明细' : '暂无分部，点击「添加分部」开始建立明细清单'}
              </Typography>
            )}

            {isArchived && quotation.notes && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                  {quotation.notes}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {!isCreate && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              费用汇总（GB50500-2013标准）
            </Typography>

            {/* 一、分部分项工程费 */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              一、分部分项工程费
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>直接工程费合计</Typography>
              <Typography>{formatYuan(quotation.directCostTotal)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>企业管理费（{quotation.managementFeeRate}%）</Typography>
              <Typography>{formatYuan(quotation.managementFee)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>利润（{quotation.profitRate}%）</Typography>
              <Typography>{formatYuan(quotation.profit)}</Typography>
            </Box>

            {/* 二、措施项目费 */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              二、措施项目费
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>技术措施费（{quotation.techMeasureRate}%）</Typography>
              <Typography>{formatYuan(quotation.techMeasureTotal || 0)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>组织措施费（{quotation.orgMeasureRate}%）</Typography>
              <Typography>{formatYuan(quotation.orgMeasureTotal || 0)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography fontWeight="bold">措施项目合计</Typography>
              <Typography fontWeight="bold">{formatYuan(quotation.measureTotal || 0)}</Typography>
            </Box>

            {/* 三、其他项目费 */}
            {(quotation.provisionalSum > 0 || quotation.materialProvisional > 0 || quotation.dayWorkAmount > 0 || quotation.contractorServiceFee > 0) && (
              <>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                  三、其他项目费
                </Typography>
                {quotation.provisionalSum > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                    <Typography>暂列金额</Typography>
                    <Typography>{formatYuan(quotation.provisionalSum)}</Typography>
                  </Box>
                )}
                {quotation.materialProvisional > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                    <Typography>材料暂估价</Typography>
                    <Typography>{formatYuan(quotation.materialProvisional)}</Typography>
                  </Box>
                )}
                {quotation.dayWorkAmount > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                    <Typography>计日工</Typography>
                    <Typography>{formatYuan(quotation.dayWorkAmount)}</Typography>
                  </Box>
                )}
                {quotation.contractorServiceFee > 0 && (
                  <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                    <Typography>总承包服务费</Typography>
                    <Typography>{formatYuan(quotation.contractorServiceFee)}</Typography>
                  </Box>
                )}
              </>
            )}

            {/* 四、规费 */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              四、规费
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>社会保险费（{quotation.socialInsuranceRate}%）</Typography>
              <Typography>{formatYuan(quotation.socialInsurance || 0)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>住房公积金（{quotation.housingFundRate}%）</Typography>
              <Typography>{formatYuan(quotation.housingFund || 0)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography>工伤保险（{quotation.workInjuryRate}%）</Typography>
              <Typography>{formatYuan(quotation.workInjury || 0)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
              <Typography fontWeight="bold">规费合计</Typography>
              <Typography fontWeight="bold">{formatYuan(quotation.regulationFee || 0)}</Typography>
            </Box>

            {/* 风险费 */}
            {(quotation.riskFee || 0) > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                  风险费
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                  <Typography>风险费{quotation.riskFeeRate > 0 ? `（${quotation.riskFeeRate}%）` : ''}</Typography>
                  <Typography>{formatYuan(quotation.riskFee)}</Typography>
                </Box>
              </>
            )}

            {/* 垫资成本 */}
            {(quotation.capitalCost || 0) > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                  垫资成本
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1} sx={{ pl: 2 }}>
                  <Typography>资金垫用成本（{quotation.capitalCostRate}%）</Typography>
                  <Typography>{formatYuan(quotation.capitalCost)}</Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* 汇总 */}
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography fontWeight="bold">税前工程造价</Typography>
              <Typography fontWeight="bold">{formatYuan(quotation.preTaxTotal)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography>增值税（{quotation.taxRate}%）</Typography>
              <Typography>{formatYuan(quotation.taxAmount)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h6">含税工程总造价</Typography>
              <Typography variant="h6" color="primary">
                {formatYuan(quotation.totalAmount)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isCreate && !isArchived && quotation.notes && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              备注
            </Typography>
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {quotation.notes}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* AI 多轮对话弹窗：沟通方案 → 生成结构 → 导入 */}
      <Dialog open={aiDialog.open} onClose={() => !aiDialog.sending && !aiDialog.generating && !aiDialog.importing && setAiDialog((d) => ({ ...d, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon color="secondary" />
            AI 报价顾问（可多轮沟通）
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            像跟助手说话一样：先把现场情况发过来，来回讨论方案和价格（你提意见、项目经理的看法都行），
            谈得差不多了点底部「按对话生成报价结构」，核对后导入本单。
          </Typography>

          {/* 对话区 */}
          <Box sx={{ maxHeight: 320, overflow: 'auto', mb: 1.5, p: 1, backgroundColor: 'grey.50' }}>
            {aiDialog.messages.length === 0 && !aiDialog.sending && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                例如：3单元2号楼顶层漏水，原落水管移到室外后洞口没堵严，402/502/2002/2902 都在渗……
              </Typography>
            )}
            {aiDialog.messages.map((m, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.2, mb: 1, backgroundColor: m.role === 'user' ? 'primary.light' : 'background.paper', maxWidth: '92%' }}>
                <Typography variant="caption" color={m.role === 'user' ? 'primary' : 'secondary'} sx={{ fontWeight: 'bold', display: 'block' }}>
                  {m.role === 'user' ? '我' : 'AI 顾问'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
              </Paper>
            ))}
            {aiDialog.sending && (
              <Box display="flex" alignItems="center" gap={1} sx={{ p: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">AI 正在回复…</Typography>
              </Box>
            )}
          </Box>

          {/* 输入区 */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="输入现场情况 / 你的意见 / 追问"
            value={aiDialog.input}
            onChange={(e) => setAiDialog((d) => ({ ...d, input: e.target.value, error: null }))}
            disabled={aiDialog.sending || aiDialog.generating || aiDialog.importing}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendAiChat(); }}
            helperText="Cmd/Ctrl + Enter 发送"
          />

          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={
              <Switch
                size="small"
                checked={aiDialog.research}
                onChange={(e) => setAiDialog((d) => ({ ...d, research: e.target.checked }))}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                联网调研（问市场行情/信息价走势时勾上；平时关闭更快。AI 始终参照咱的历史成交锚点报价）
              </Typography>
            }
          />

          {aiDialog.error === 'NO_KEY' ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              还没有配置 AI API Key。请先到
              <Button size="small" href="#/ai-settings" onClick={() => setAiDialog((d) => ({ ...d, open: false }))}>AI 设置页</Button>
              把阿里云百炼的 Key 保存到服务器（一次配置，手机电脑都能用），然后回来继续。
            </Alert>
          ) : aiDialog.error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>{aiDialog.error}</Alert>
          ) : null}

          {(aiDialog.questions?.length > 0) && !aiDialog.result && (
            <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
              <Typography variant="subtitle2">成单前核对（AI 从讨论中识别的疑点）</Typography>
              {aiDialog.questions.map((q, i) => (
                <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
                  {q.level === 'must' ? '【必须确认】' : '【可暂估】'}{q.topic}：{q.question}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary">在下方输入框逐条答复后重新核对，或确认无误后生成结构</Typography>
            </Box>
          )}

          {aiDialog.result && (
            <Box sx={{ mt: 1.5 }}>
              <Alert severity="success" sx={{ mb: 1.5 }}>
                结构已生成：<strong>{aiDialog.result.sections?.length || 0}</strong> 个分部、
                共 <strong>{(aiDialog.result.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0)}</strong> 个明细行。
                核对无误后点「导入本单」；要改就继续在上方对话里说，然后重新生成。
              </Alert>
              {(aiDialog.warnings || []).length > 0 && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>⚠ 价格护栏预警（{aiDialog.warnings.length} 条）——请逐条核对再导入：</Typography>
                  {aiDialog.warnings.map((w, k) => (
                    <Typography key={k} variant="caption" display="block" sx={{ whiteSpace: 'pre-wrap' }}>
                      {w.type === 'missing' ? '缺价' : `偏离+${w.deviationPct}%`}：{w.message}
                    </Typography>
                  ))}
                </Alert>
              )}
              {(aiDialog.result.sections || []).map((s, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="subtitle2">
                    {i + 1}. {s.name}（{s.items?.length || 0} 行）
                  </Typography>
                  {(s.items || []).slice(0, 5).map((it, j) => {
                    const warn = (aiDialog.warnings || []).find((w) => w.sectionIndex === i && w.itemIndex === j);
                    return (
                      <Typography key={j} variant="caption" display="block" color={warn ? 'error' : 'text.secondary'} sx={warn ? { fontWeight: 'bold' } : undefined}>
                        　{warn ? '🔴 ' : ''}{it.name}｜{it.unit}｜数量 {it.quantity}｜人材机 {it.materialPriceYuan || 0}/{it.laborPriceYuan || 0}/{it.machineryPriceYuan || 0}
                      </Typography>
                    );
                  })}
                  {(s.items?.length || 0) > 5 && (
                    <Typography variant="caption" color="text.secondary">　…其余 {(s.items?.length || 0) - 5} 行导入后可见</Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiDialog((d) => ({ ...d, open: false }))} disabled={aiDialog.sending || aiDialog.generating || aiDialog.importing}>
            关闭
          </Button>
          {!aiDialog.result && (
            <Button
              variant="outlined"
              color="warning"
              onClick={runClarify}
              disabled={aiDialog.clarifying || aiDialog.generating || aiDialog.sending || aiDialog.messages.length < 2}
              startIcon={aiDialog.clarifying ? <CircularProgress size={18} /> : null}
            >
              {aiDialog.clarifying ? '核对中…' : '先核对疑点'}
            </Button>
          )}
          {!aiDialog.result && (
            <Button
              variant="contained"
              color="secondary"
              onClick={generateFromChat}
              disabled={aiDialog.generating || aiDialog.sending || aiDialog.messages.length < 2}
              startIcon={aiDialog.generating ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
            >
              {aiDialog.generating ? '生成中…' : '按对话生成报价结构'}
            </Button>
          )}
          {aiDialog.result && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAiImport}
              disabled={aiDialog.importing}
              startIcon={aiDialog.importing ? <CircularProgress size={18} /> : null}
            >
              {aiDialog.importing ? '导入中…' : '导入本单'}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={sendAiChat}
            disabled={aiDialog.sending || aiDialog.generating || !aiDialog.input.trim()}
          >
            发送
          </Button>
        </DialogActions>
      </Dialog>

      {/* 单价库 / 信息价选择弹窗 */}
      <Dialog
        open={quotaPicker.open}
        onClose={() => setQuotaPicker({ open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" gap={1} alignItems="center">
            <Button size="small" variant={pickerTab === 'quota' ? 'contained' : 'outlined'} onClick={() => openQuotaPicker('quota')}>
              单价库（{quotaItems.length} 条：历史报价 + 参考区间）
            </Button>
            <Button size="small" variant={pickerTab === 'info' ? 'contained' : 'outlined'} onClick={() => openQuotaPicker('info')}>
              山西信息价（{infoPrices.length} 条）
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pickerTab === 'quota' ? (
          <>
          <TextField
            label="搜索名称 / 规格 / 来源工程 / 分类"
            value={quotaSearch}
            onChange={(e) => setQuotaSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            autoFocus
          />
          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>名称</TableCell>
                  <TableCell>规格</TableCell>
                  <TableCell>单位</TableCell>
                  <TableCell align="right">材料</TableCell>
                  <TableCell align="right">人工</TableCell>
                  <TableCell align="right">机械</TableCell>
                  <TableCell align="right">综合价</TableCell>
                  <TableCell>来源工程</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {quotaFiltered.slice(0, PICKER_MAX_ROWS).map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.name}</TableCell>
                    <TableCell>{it.specification || '-'}</TableCell>
                    <TableCell>{it.unit}</TableCell>
                    <TableCell align="right">{Number(it.materialPrice) > 0 ? Number(it.materialPrice).toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">{Number(it.laborPrice) > 0 ? Number(it.laborPrice).toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">{Number(it.machineryPrice) > 0 ? Number(it.machineryPrice).toFixed(2) : '-'}</TableCell>
                    <TableCell align="right">{Number(it.comprehensivePrice) > 0 ? Number(it.comprehensivePrice).toFixed(2) : '-'}</TableCell>
                    <TableCell>{it.projectName || it.category}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => pickQuota(it)}>带入</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {quotaFiltered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography align="center" color="text.secondary">没有匹配条目</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {quotaFiltered.length > 100 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              仅显示前 {PICKER_MAX_ROWS} 条（共 {quotaFiltered.length} 条），请细化搜索
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            带入口径：三价齐全的条目带入人材机裸价，保存后由系统费率链（管理/利润/组织措施/税）统一计价；
            仅存综合价的条目（历史全费用口径）只带入名称/规格/单位——综合价已含费率，直接拆价会被再计价约 22%，请手工填裸价。
          </Typography>
          </>
          ) : (
            <>
          <TextField
            label="搜索材料名称"
            value={infoSearch}
            onChange={(e) => setInfoSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
            autoFocus
          />
          <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>材料名称</TableCell>
                  <TableCell>规格</TableCell>
                  <TableCell>单位</TableCell>
                  <TableCell>期次</TableCell>
                  <TableCell align="right">含税价（元）</TableCell>
                  <TableCell align="right">不含税价（元）</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {infoPrices
                  .filter((it) => !infoSearch || (it.materialName || '').includes(infoSearch))
                  .slice(0, PICKER_MAX_ROWS)
                  .map((it) => (
                    <TableRow key={it.id} hover>
                      <TableCell>{it.materialName}</TableCell>
                      <TableCell>{it.specification || '-'}</TableCell>
                      <TableCell>{it.unit}</TableCell>
                      <TableCell>{it.period}</TableCell>
                      <TableCell align="right">{Number(it.priceTaxed).toFixed(2)}</TableCell>
                      <TableCell align="right">{it.priceUntaxed != null ? Number(it.priceUntaxed).toFixed(2) : '-'}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => pickInfoPrice(it)}>带入材料价</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                {infoPrices.filter((it) => !infoSearch || (it.materialName || '').includes(infoSearch)).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography align="center" color="text.secondary">
                        没有匹配条目{infoPrices.length === 0 ? '（信息价库为空：先跑 scripts/fetch_info_prices.py 抓取）' : ''}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            信息价是山西住建厅官方发布的材料含税价，带入后自动落依据（来源+期次）进项目特征；
            官方价只覆盖材料费，人工/机械价仍需按定额或询价补填。
          </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 工艺模板向导（B1）：选模板 → 填参数 → 后端求值展开（含历史成交依据+防水基层条款） */}
      <Dialog open={assemblyDlg.open} onClose={() => setAssemblyDlg((d) => ({ ...d, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle>从工艺模板添加分部</DialogTitle>
        <DialogContent>
          {assemblyDlg.templates === null ? (
            <Box py={4} display="flex" justifyContent="center"><CircularProgress /></Box>
          ) : assemblyDlg.templates.length === 0 ? (
            <Alert severity="info">
              还没有工艺模板。管理员在服务器上执行 npm run seed:assemblies 可安装三套种子模板
              （屋面SBS/室外排水管/落水管，历史成交价）；也可在「单价库」页维护。
            </Alert>
          ) : !assemblyDlg.selected ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>模板</TableCell>
                    <TableCell>类别</TableCell>
                    <TableCell>参数</TableCell>
                    <TableCell>行数</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assemblyDlg.templates.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.description}</Typography>
                      </TableCell>
                      <TableCell>{t.category}</TableCell>
                      <TableCell>{(t.paramsSchema || []).map((p) => p.label || p.key).join('、') || '—'}</TableCell>
                      <TableCell>{(t.itemsTemplate || []).length}</TableCell>
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => {
                          const defaults = {};
                          (t.paramsSchema || []).forEach((p) => { defaults[p.key] = p.default ?? ''; });
                          setAssemblyDlg((d) => ({ ...d, selected: t, params: defaults }));
                        }}>选用</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{assemblyDlg.selected.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                行数与单价可展开后逐行微调；每行自带历史成交依据（出门闸门可过），防水类自动附基层风险条款。
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                {(assemblyDlg.selected.paramsSchema || []).map((p) => (
                  <TextField
                    key={p.key} size="small" type="number" label={p.label || p.key}
                    sx={{ width: 200 }} value={assemblyDlg.params[p.key] ?? ''}
                    onChange={(e) => setAssemblyDlg((d) => ({ ...d, params: { ...d.params, [p.key]: e.target.value } }))}
                  />
                ))}
              </Box>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>行</TableCell><TableCell>单位</TableCell><TableCell>数量表达式</TableCell>
                      <TableCell align="right">材料</TableCell><TableCell align="right">人工</TableCell><TableCell align="right">机械</TableCell><TableCell>依据</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(assemblyDlg.selected.itemsTemplate || []).map((it, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{it.name}</TableCell>
                        <TableCell>{it.unit}</TableCell>
                        <TableCell>{it.quantityExpr || '1'}</TableCell>
                        <TableCell align="right">{Number(it.materialPrice) || 0}</TableCell>
                        <TableCell align="right">{Number(it.laborPrice) || 0}</TableCell>
                        <TableCell align="right">{Number(it.machineryPrice) || 0}</TableCell>
                        <TableCell>{it.priceSourceRef || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {assemblyDlg.selected && (
            <Button onClick={() => setAssemblyDlg((d) => ({ ...d, selected: null }))}>返回列表</Button>
          )}
          <Button onClick={() => setAssemblyDlg((d) => ({ ...d, open: false }))}>取消</Button>
          {assemblyDlg.selected && (
            <Button variant="contained" onClick={applyAssembly} disabled={assemblyDlg.applying}
              startIcon={assemblyDlg.applying ? <CircularProgress size={16} /> : null}>
              {assemblyDlg.applying ? '展开中…' : '展开到本单'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 修订历史抽屉（B2）：版本列表 + 与当前对比 */}
      <Drawer anchor="right" open={revDrawer.open} onClose={() => setRevDrawer({ open: false, list: [], diff: null, loading: false })}>
        <Box sx={{ width: { xs: 340, sm: 480 }, p: 2 }} role="presentation">
          <Typography variant="h6" gutterBottom>修订历史</Typography>
          {revDrawer.loading && <CircularProgress size={20} sx={{ mb: 1 }} />}
          {!revDrawer.loading && revDrawer.list.length === 0 && (
            <Alert severity="info">暂无修订版。点「存修订版」手动存档；跑 export_and_check.sh 发单会自动存「发单版」。</Alert>
          )}
          {revDrawer.list.map((r) => (
            <Paper key={r.id} sx={{ p: 1.5, mb: 1 }} variant="outlined">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">rev{r.revNo} · {formatYuan(r.totalAmountYuan || 0)}</Typography>
                <Button size="small" onClick={() => loadRevisionDiff(r.id)}>对比当前</Button>
              </Box>
              <Typography variant="caption" color="text.secondary">{r.changeNote} · {String(r.createdAt || '').slice(0, 16).replace('T', ' ')}</Typography>
            </Paper>
          ))}
          {revDrawer.diff && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                rev{revDrawer.diff.revision.revNo} → 当前（{revDrawer.diff.revision.changeNote}）
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  含税总价：{formatYuan(revDrawer.diff.totals.totalAmount.from || 0)} → <b>{formatYuan(revDrawer.diff.totals.totalAmount.to || 0)}</b>
                  （明细行 {revDrawer.diff.totals.itemCount.from} → {revDrawer.diff.totals.itemCount.to}）
                </Typography>
                {revDrawer.diff.rateChanges.map((c) => (
                  <Typography key={c.field} variant="caption" color="warning.main" display="block">
                    费率 {c.field}：{c.from}% → {c.to}%
                  </Typography>
                ))}
              </Box>
              {revDrawer.diff.itemChanges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">明细行无差异（仅表头/费率变化，或本版即当前态）</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow><TableCell>变动</TableCell><TableCell>行</TableCell><TableCell>明细</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                      {revDrawer.diff.itemChanges.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Chip size="small" label={c.type === 'added' ? '新增' : c.type === 'removed' ? '删除' : '改动'}
                              color={c.type === 'added' ? 'success' : c.type === 'removed' ? 'error' : 'warning'} variant="outlined" />
                          </TableCell>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>
                            {c.type === 'changed'
                              ? Object.entries(c.fields).map(([f, v]) => `${f}: ${v.from}→${v.to}`).join('；')
                              : `${c.unitPrice}元 × ${c.quantity} = ${formatYuan(c.totalPrice || 0)}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}
